// The Standards Library backend. Same shape as TOSP's client: one request helper, typed errors, and
// nothing clever — the interesting work is on the other side of the wire.

import {
  AuditEntry,
  ConfirmedStandard,
  PublishedRuleSet,
  ReadResult,
  ProgressSnapshot,
  ReviewState,
  RuleEdit,
  RulePart,
  HumanDecision,
  SectionReviewState,
  StructureAssessment,
  VerityRule,
  VerityStandard,
  VeritySection,
} from "./types";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/verity`;

export class VerityApiError extends Error {
  status: number;

  /**
   * The structure assessment, when the server refused because the section tree looks wrong.
   *
   * Carried on the error rather than thrown away with the body, because the caller has to be able to
   * show WHY it was held and offer to proceed anyway — a bare "409" cannot do either.
   */
  structure?: StructureAssessment;

  constructor(message: string, status: number, structure?: StructureAssessment) {
    super(message);
    this.name = "VerityApiError";
    this.status = status;
    this.structure = structure;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // `...init` must come BEFORE headers, or its own `headers` key replaces the merged object outright.
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    // The whole body, not just its message: a structure refusal carries the assessment that explains
    // it, and the caller has to be able to show that rather than a bare status code.
    const body = (await res.json().catch(() => undefined)) as
      | { error?: string; structure?: StructureAssessment }
      | undefined;
    throw new VerityApiError(
      body?.error || `Request failed (${res.status})`,
      res.status,
      body?.structure,
    );
  }

  return (await res.json()) as T;
}

export async function listStandards(): Promise<VerityStandard[]> {
  const { items } = await request<{ items: VerityStandard[] }>("/standards");
  return items || [];
}

/**
 * Step 1 — reads a document and proposes what it is.
 *
 * Adds nothing to the library. The file is staged and extracted so step 2 costs nothing, but a review
 * that is abandoned here leaves no row behind.
 */
export async function readStandardDocuments(files: File[]): Promise<ReadResult> {
  const form = new FormData();
  // One standard, in the order its parts were added — part 0 carries the cover page.
  for (const file of files) form.append("files", file);

  const res = await fetch(`${BASE}/standards/read`, {
    credentials: "include",
    method: "POST",
    // No Content-Type: the browser has to set the multipart boundary itself.
    body: form,
  });

  if (!res.ok) {
    const detail = await res
      .json()
      .then((body) => (body as { error?: string }).error)
      .catch(() => undefined);
    throw new VerityApiError(detail || `Could not read the document (${res.status})`, res.status);
  }

  return (await res.json()) as ReadResult;
}

/**
 * Step 2 — adds the confirmed standard to the library.
 *
 * Sends the values a PERSON verified, never the ones the reader proposed, along with the list of
 * fields they signed off on. The section tree is built server-side from the already-staged document,
 * so a confirmed standard arrives structured.
 */
export async function createStandard(confirmed: ConfirmedStandard): Promise<VerityStandard> {
  const { standard } = await request<{ standard: VerityStandard }>("/standards", {
    method: "POST",
    body: JSON.stringify(confirmed),
  });
  return standard;
}

/** Extracts the canonical text and builds the section tree. Safe to re-run; both outputs are derived. */
export async function extractStandard(id: string): Promise<VerityStandard> {
  const { standard } = await request<{ standard: VerityStandard }>(`/standards/${id}/extract`, {
    method: "POST",
  });
  return standard;
}

/**
 * Where a standard's source document lives.
 *
 * A plain URL rather than a fetch: the backend streams the PDF inline under the caller's own
 * permission, and the auth cookie is SameSite lax/none, so a top-level navigation carries it. Opening
 * the real document in a real tab beats rendering a copy of it in ours.
 */
export function documentUrl(id: string, docIndex = 0): string {
  return `${BASE}/standards/${id}/file/${docIndex}`;
}

export async function listSections(id: string): Promise<VeritySection[]> {
  const { sections } = await request<{ sections: VeritySection[] }>(`/standards/${id}/sections`);
  return sections || [];
}

export async function readSectionText(
  id: string,
  sectionId: string,
): Promise<{ section: VeritySection; text: string }> {
  return request(`/standards/${id}/sections/${encodeURIComponent(sectionId)}/text`);
}

export async function deleteStandard(id: string): Promise<void> {
  await request<void>(`/standards/${id}`, { method: "DELETE" });
}

// ─── Requirements: extraction, review, publication ─────────────────────────────

/**
 * Extracts candidate requirements. Chapter-scoped by default — a 300-section standard would otherwise
 * make the first click a five-minute wait before anything useful appeared.
 */
export async function extractRules(
  id: string,
  sections?: string[],
  force = false,
): Promise<{ rulesExtracted: number; sectionsRun: number; sectionsCached: number; rulesRejected: number; rules: VerityRule[] }> {
  return request(`/standards/${id}/rules/extract`, {
    method: "POST",
    body: JSON.stringify({ sections, force }),
  });
}

/**
 * Progress without the rule bodies.
 *
 * Polling the full review payload put ~23 MB/min through the same process that was doing the
 * extracting, and polls began overlapping. This returns counts and per-section rollups only.
 */
export async function loadProgress(id: string): Promise<ProgressSnapshot> {
  return request(`/standards/${id}/progress`);
}

/** Rules, per-section progress and published sets in one read — they are always wanted together. */
export async function loadReview(id: string): Promise<ReviewState> {
  return request(`/standards/${id}/review`);
}

export async function listAudit(id: string): Promise<AuditEntry[]> {
  const { entries } = await request<{ entries: AuditEntry[] }>(`/standards/${id}/audit`);
  return entries || [];
}

export async function setRuleDecision(
  id: string,
  ruleId: string,
  state: HumanDecision,
  note?: string,
): Promise<VerityRule> {
  const { rule } = await request<{ rule: VerityRule }>(`/standards/${id}/rules/${ruleId}/state`, {
    method: "POST",
    body: JSON.stringify({ state, note }),
  });
  return rule;
}

export async function editRule(id: string, ruleId: string, edit: RuleEdit): Promise<VerityRule> {
  const { rule } = await request<{ rule: VerityRule }>(`/standards/${id}/rules/${ruleId}`, {
    method: "PUT",
    body: JSON.stringify(edit),
  });
  return rule;
}

/** Splits one candidate into several. The parent survives, excluded, pointing at its children. */
export async function splitRule(id: string, ruleId: string, parts: RulePart[]): Promise<VerityRule[]> {
  const { rules } = await request<{ rules: VerityRule[] }>(`/standards/${id}/rules/${ruleId}/split`, {
    method: "POST",
    body: JSON.stringify({ parts }),
  });
  return rules;
}

export async function mergeRules(id: string, ruleIds: string[], merged: RulePart): Promise<VerityRule> {
  const { rule } = await request<{ rule: VerityRule }>(`/standards/${id}/rules/merge`, {
    method: "POST",
    body: JSON.stringify({ ruleIds, merged }),
  });
  return rule;
}

export async function setSectionReview(
  id: string,
  sectionId: string,
  state: SectionReviewState,
  note?: string,
): Promise<void> {
  await request(`/standards/${id}/sections/${encodeURIComponent(sectionId)}/review`, {
    method: "POST",
    body: JSON.stringify({ state, note }),
  });
}

/**
 * Settles one recovery candidate on a possible extraction gap.
 *
 * Accepting mints the proposed requirement through the same quote gate as any other rule, so the
 * reviewer's decision is "yes, that reading is right", not "please create this for me".
 */
export async function resolveRecovery(
  id: string,
  sectionId: string,
  candidateId: string,
  action: "accept" | "reject",
  edits?: { subject?: string; requirement?: string },
): Promise<void> {
  await request(
    `/standards/${id}/sections/${encodeURIComponent(sectionId)}/recovery/${encodeURIComponent(candidateId)}`,
    { method: "POST", body: JSON.stringify({ action, edits }) },
  );
}

/**
 * Starts reading the whole standard, and returns at once.
 *
 * The work runs on the server, so leaving the page does not stop it. Safe to call again — a run
 * already in flight is left alone.
 */
/**
 * Starts reading a standard's requirements.
 *
 * Refused with a 409 carrying a StructureAssessment when the section tree does not look believable
 * for a document that size. `acknowledgeStructure` is a person saying they have looked and want it
 * run anyway — the gate exists to make somebody look, not to have the final word.
 */
export async function startProcessing(
  id: string,
  acknowledgeStructure = false,
): Promise<{ started: boolean; sectionsTotal: number }> {
  return request(`/standards/${id}/process`, {
    method: "POST",
    body: JSON.stringify({ acknowledgeStructure }),
  });
}

/** Publishes what has actually been reviewed, with coverage recorded rather than implied. */
export async function publishRuleSet(id: string): Promise<PublishedRuleSet> {
  const { ruleSet } = await request<{ ruleSet: PublishedRuleSet }>(`/standards/${id}/publish`, {
    method: "POST",
  });
  return ruleSet;
}

/**
 * Asks a run in flight to stop.
 *
 * Cooperative: the section already with the model finishes and is stored, so nothing already paid for
 * is thrown away, and a later Resume picks up only what was left.
 */
export async function stopProcessing(id: string): Promise<void> {
  await request(`/standards/${id}/stop`, { method: "POST" });
}
